import { useMemo } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { type AccessCode, type User } from 'types';

const holdsEveryCode = (user: User, codes: AccessCode[]): boolean => {
  const held = user.permissionCodes ?? [];

  return codes.every((code) => held.includes(code));
};

/**
 * Whether the current user holds every requested code. The api answers which
 * codes the caller holds (`permissionCodes` on GET/PUT /user/current, computed by
 * the same dispatch its route guards use), so the app derives nothing from roles
 * and applies no implications of its own. `permitted` is undefined until the user
 * is loaded; the memo keys on the array's identity. Stays outside
 * hooks/useCurrentUser so specs can drive it by mocking that module.
 */
export const useHasPermissions = ({ permissions }: { permissions: AccessCode[] }) => {
  const { user } = useCurrentUser()

  const permitted = useMemo(() => {
    if (!user) {
      return
    }
    return holdsEveryCode(user, permissions)
  }, [user, permissions]);

  return { permitted, checking: permitted === undefined }
}
